import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { fhirclient } from 'fhirclient/lib/types';
import { AppContext } from 'src/app/models/AppContext';
import { ResourceType } from 'src/app/models/ResourceType';
import { ContextService } from './context.service';

@Injectable({
  providedIn: 'root',
})
export class ParameterParserService {
  private readonly contextService = inject(ContextService);

  readonly cqfLibExtensionUrl =
    'http://hl7.org/fhir/StructureDefinition/cqf-library';
  static readonly dtrLogErrorsExtensionProfile =
    'http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/dtr-log-errors-input-parameters';
  static readonly launchContextExtUrl =
    'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-launchContext';

  getCqfParams(
    questionnaire: fhirR4.Questionnaire,
    qPackage: fhirR4.Bundle,
  ): fhirR4.ParameterDefinition[] | undefined {
    return this.getCqfLib(qPackage, this.getCqfLibUrl(questionnaire))
      ?.parameter;
  }

  getCqfLib(qPackage: fhirR4.Bundle, url: string): fhirR4.Library {
    return qPackage.entry?.find(
      (entry) =>
        entry.resource?.resourceType === ResourceType.Library &&
        (entry.resource as fhirR4.Library).url === url,
    )?.resource as fhirR4.Library;
  }

  getCqfLibUrl(questionnaire: fhirR4.Questionnaire): string {
    return (
      questionnaire.extension?.find(
        (ext) => ext.url === this.cqfLibExtensionUrl,
      )?.valueCanonical || ''
    );
  }

  createParametersParameterResource(
    name: string | undefined,
    value: string | fhirR4.Reference | fhirR4.Resource | undefined,
    extension?: fhirR4.Extension[],
  ): fhirR4.ParametersParameter {
    const retVal = new fhirR4.ParametersParameter();
    if (!value || !name) {
      return retVal;
    }
    retVal.name = name;
    if (typeof value === 'string') {
      retVal.valueString = value;
    } else if (value instanceof fhirR4.Reference) {
      retVal.valueReference = value;
    } else {
      retVal.resource = value;
    }
    if (extension) {
      retVal.extension = extension;
    }
    return retVal;
  }

  createParameterForPopulate(
    questionnaire: fhirR4.Questionnaire,
    appContext: AppContext,
    cqfParams: fhirR4.ParameterDefinition[] | undefined,
    coverageId: string,
    state: fhirclient.ClientState | undefined,
    patient: fhirR4.Reference,
  ): fhirR4.Parameters {
    const retVal = new fhirR4.Parameters();
    retVal.resourceType = ResourceType.Parameters;

    const parameters = [
      this.createQuestionnaireParam(questionnaire),
      this.createParametersParameterResource('subject', patient),
      this.createParametersParam(appContext, cqfParams, coverageId),
      this.createEndpointParam(state),
    ].concat(this.createContextParam(questionnaire, patient));
    retVal.parameter = parameters.filter((element) => element?.name);
    return retVal;
  }

  createQuestionnaireParam(
    questionnaire: fhirR4.Questionnaire,
  ): fhirR4.ParametersParameter {
    return this.createParametersParameterResource(
      'questionnaire',
      questionnaire,
    );
  }

  createParametersParam(
    appContext: AppContext,
    cqfParams: fhirR4.ParameterDefinition[] | undefined,
    coverageId: string,
  ): fhirR4.ParametersParameter {
    const orderParams = this.createOrderParams(appContext, cqfParams);
    orderParams.parameter?.push(
      this.createParametersParameterResource('Coverage Id', coverageId),
    );

    return this.createParametersParameterResource(
      'parameters',
      orderParams as unknown as fhirR4.Resource, //needed because fhirts Parameters are not counted as Resources
    );
  }

  createOrderParams(
    appContext: AppContext,
    cqfParams: fhirR4.ParameterDefinition[] | undefined,
  ): fhirR4.Parameters {
    const paramTypeTracker: Set<string> = new Set();
    const orderParams = new fhirR4.Parameters();
    orderParams.resourceType = ResourceType.Parameters;
    orderParams.parameter = appContext.fhirContext
      ? this.createOrderParamsUsingFhirContext(
          appContext,
          cqfParams,
          paramTypeTracker,
        )
      : this.createOrderParamsUsingAppContext(
          appContext,
          cqfParams,
          paramTypeTracker,
        );
    return orderParams;
  }

  createOrderParamsUsingFhirContext(
    appContext: AppContext,
    cqfParams: fhirR4.ParameterDefinition[] | undefined,
    paramTypeTracker: Set<string>,
  ): fhirR4.ParametersParameter[] {
    const [orderType, orderId] = this.contextService
      .getOrderReferenceFromMarshalContext(appContext.marshalContext)
      .split('/');
    let type;
    switch (orderType) {
      case ResourceType.ServiceRequest:
        type = 'Service Request Id';
        break;
      case ResourceType.DeviceRequest:
        type = 'Device Request Id';
        break;
      case ResourceType.NutritionOrder:
        type = 'Nutrition Order Id';
        break;
    }
    return [
      this.createParametersParameterResource(
        type,
        orderId,
        this.addExtensionIfFirstInstance(
          cqfParams?.find((param) => param.name === type),
          paramTypeTracker,
        ),
      ),
    ];
  }

  createOrderParamsUsingAppContext(
    appContext: AppContext,
    cqfParams: fhirR4.ParameterDefinition[] | undefined,
    paramTypeTracker: Set<string>,
  ): fhirR4.ParametersParameter[] | undefined {
    return appContext.context?.draftOrders.entry?.map((order) => {
      const cqfParam = this.getParamNameBasedOnType(
        cqfParams,
        order.resource?.resourceType,
      );
      return this.createParametersParameterResource(
        cqfParam?.name,
        order.resource,
        this.addExtensionIfFirstInstance(cqfParam, paramTypeTracker),
      );
    });
  }

  getParamNameBasedOnType(
    cqfParams: fhirR4.ParameterDefinition[] | undefined,
    type: string | undefined,
  ) {
    return cqfParams?.find((param) => param.type === type);
  }

  addExtensionIfFirstInstance(
    cqfParam: fhirR4.ParameterDefinition | undefined,
    paramTypeTracker: Set<string>,
  ): fhirR4.Extension[] | undefined {
    if (cqfParam?.name && !paramTypeTracker.has(cqfParam.name)) {
      paramTypeTracker.add(cqfParam.name);
      return [this.createSingleEntryExtension(cqfParam)];
    }
    return undefined;
  }

  createSingleEntryExtension(
    cqfParam: fhirR4.ParameterDefinition,
  ): fhirR4.Extension {
    const extension = new fhirR4.Extension();
    extension.url =
      'http://hl7.org/fhir/uv/cpg/StructureDefinition/cpg-parameterDefinition';
    extension.valueParameterDefinition = cqfParam;
    return extension;
  }

  createEndpointParam(
    state: fhirclient.ClientState | undefined,
  ): fhirR4.ParametersParameter {
    const endpoint = new fhirR4.Endpoint();
    endpoint.resourceType = ResourceType.Endpoint;
    endpoint.address = state?.serverUrl;
    endpoint.header = [
      'Authorization: Bearer ' + state?.tokenResponse?.access_token,
    ];
    return this.createParametersParameterResource('dataEndpoint', endpoint);
  }

  createContextParam(
    questionnaire: fhirR4.Questionnaire,
    patient: fhirR4.Reference,
  ): fhirR4.ParametersParameter[] {
    const contextExt = questionnaire.extension?.filter(
      (ext) => ext.url === ParameterParserService.launchContextExtUrl,
    );
    return (
      contextExt?.map((context) => {
        const retVal = new fhirR4.ParametersParameter();
        retVal.name = 'context';
        retVal.part = [
          {
            name: 'name',
            valueString: context.extension?.find((ext) => ext.url === 'name')
              ?.valueCoding?.code,
          },
          {
            name: 'content',
            valueReference: patient,
          },
        ];
        return retVal;
      }) || []
    );
  }

  createPopulateErrorLogParameters(
    payload: fhirR4.QuestionnaireResponse,
  ): fhirR4.Parameters | undefined {
    const operationOutcomes = payload['contained']
      ?.filter(
        (containedResource) =>
          containedResource.resourceType === ResourceType.OperationOutcome,
      )
      .map((outcome) => {
        return { name: 'operationOutcome', resource: outcome };
      });

    if (operationOutcomes && operationOutcomes.length > 0) {
      return {
        resourceType: ResourceType.Parameters,
        meta: {
          profile: [ParameterParserService.dtrLogErrorsExtensionProfile],
        },
        parameter: [
          { name: 'questionnaire', valueCanonical: payload.questionnaire },
          ...operationOutcomes,
        ],
      } as fhirR4.Parameters;
    }
    return undefined;
  }
}
